/**
 * What the Subscriptions page shows, worked out from the API's own records.
 * Nothing here invents a number: a stat with no data behind it is null and
 * the page hides it.
 */
import type { Payout, Subscription, Transaction, UserBrief } from '../../types'

export const formatCents = (cents: number, currency = 'USD'): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(
    cents / 100
  )

/** What one billing period of the subscription costs. */
export const priceCents = (sub: Subscription): number | null => {
  if (!sub.tier) return null
  if (sub.is_annual) return sub.tier.price_yearly_cents ?? null
  return sub.tier.price_monthly_cents
}

const periodEnded = (sub: Subscription, now: Date) => new Date(sub.current_period_end) <= now

/** Still on the list: not expired, and a cancelled one only until its paid period ends. */
export const isCurrent = (sub: Subscription, now = new Date()): boolean => {
  if (sub.status === 'expired') return false
  if (sub.status === 'cancelled') return !periodEnded(sub, now)
  return true
}

/** Will be charged again at the end of this period. */
export const renews = (sub: Subscription): boolean =>
  (sub.status === 'active' || sub.status === 'trialing') && !sub.cancel_at_period_end

/** Has access now: active or on trial (cancelling included), or cancelled with time left. */
export const hasAccess = (sub: Subscription, now = new Date()): boolean =>
  sub.status === 'active' ||
  sub.status === 'trialing' ||
  (sub.status === 'cancelled' && !periodEnded(sub, now))

export type BadgeKind = 'active' | 'trial' | 'cancels' | 'past_due' | 'paused' | 'ended'

export const statusBadge = (sub: Subscription, now = new Date()): { kind: BadgeKind; endsAt?: string } => {
  if (sub.status === 'past_due') return { kind: 'past_due' }
  if (sub.status === 'paused') return { kind: 'paused' }
  if (!isCurrent(sub, now)) return { kind: 'ended' }
  if (sub.cancel_at_period_end || sub.status === 'cancelled') {
    return { kind: 'cancels', endsAt: sub.current_period_end }
  }
  return { kind: sub.status === 'trialing' ? 'trial' : 'active' }
}

export interface SubscriptionSummary {
  activeCount: number
  /** The soonest renewal: its date and what's charged then (renewals that day added up). */
  nextCharge: { date: string; amountCents: number } | null
  /** Renewing subscriptions per month; a yearly plan counts as a twelfth of its price. */
  monthlyCents: number | null
  includesYearly: boolean
}

export const subscriptionSummary = (subs: Subscription[], now = new Date()): SubscriptionSummary => {
  const renewing = subs.filter((sub) => renews(sub) && priceCents(sub) != null)

  let nextCharge: SubscriptionSummary['nextCharge'] = null
  if (renewing.length > 0) {
    const day = (iso: string) => iso.slice(0, 10)
    const soonest = renewing.reduce((a, b) =>
      new Date(b.current_period_end) < new Date(a.current_period_end) ? b : a
    )
    const sameDay = renewing.filter((sub) => day(sub.current_period_end) === day(soonest.current_period_end))
    nextCharge = {
      date: soonest.current_period_end,
      amountCents: sameDay.reduce((sum, sub) => sum + (priceCents(sub) ?? 0), 0),
    }
  }

  const monthlyCents =
    renewing.length > 0
      ? Math.round(
          renewing.reduce((sum, sub) => {
            const price = priceCents(sub) ?? 0
            return sum + (sub.is_annual ? price / 12 : price)
          }, 0)
        )
      : null

  return {
    activeCount: subs.filter((sub) => hasAccess(sub, now)).length,
    nextCharge,
    monthlyCents,
    includesYearly: renewing.some((sub) => sub.is_annual),
  }
}

// ── Transactions ─────────────────────────────────────────────────────────────

export type TransactionKind = 'subscription' | 'tip' | 'payout' | 'refund' | 'purchase'
export type TransactionStatus = 'paid' | 'failed' | 'refunded' | 'pending' | 'disputed'

export interface TransactionRow {
  key: string
  kind: TransactionKind
  /** "Signal package" etc. for purchases; otherwise the kind's own name. */
  label: string
  date: string
  /** The creator you paid, or who paid you; null for a payout to your bank. */
  counterparty: UserBrief | null
  amountCents: number
  currency: string
  /** out: you paid; in: you were paid (or refunded); payout: to your bank. */
  direction: 'out' | 'in' | 'payout'
  status: TransactionStatus
  description: string | null
}

const PURCHASE_LABELS: Record<string, string> = {
  COURSE: 'Course',
  SIGNAL_PACKAGE: 'Signal package',
  LIVE_SESSION: 'Live session',
}

const PAYMENT_STATUS: Record<Transaction['status'], TransactionStatus> = {
  completed: 'paid',
  failed: 'failed',
  refunded: 'refunded',
  pending: 'pending',
  processing: 'pending',
  disputed: 'disputed',
}

const PAYOUT_STATUS: Record<Payout['status'], TransactionStatus> = {
  PAID: 'paid',
  FAILED: 'failed',
  PENDING: 'pending',
  PROCESSING: 'pending',
}

export const toTransactionRows = (
  transactions: Transaction[],
  payouts: Payout[] = [],
  currentUserId?: number
): TransactionRow[] => {
  const payments = transactions.map((tx): TransactionRow => {
    const paidByMe = currentUserId == null || tx.payer_id === currentUserId
    const refunded = tx.status === 'refunded'
    const kind: TransactionKind = refunded
      ? 'refund'
      : tx.payment_type === 'SUBSCRIPTION'
      ? 'subscription'
      : tx.payment_type === 'TIP'
      ? 'tip'
      : 'purchase'
    return {
      key: `payment-${tx.id}`,
      kind,
      label: kind === 'purchase' ? PURCHASE_LABELS[tx.payment_type] ?? 'Purchase' : KIND_LABELS[kind],
      date: (refunded && tx.refunded_at) || tx.created_at,
      counterparty: (paidByMe ? tx.recipient : tx.payer) ?? null,
      amountCents: tx.amount_cents,
      currency: tx.currency || 'USD',
      // A refund moves the money back the other way.
      direction: paidByMe !== refunded ? 'out' : 'in',
      status: PAYMENT_STATUS[tx.status] ?? 'pending',
      description: tx.description ?? null,
    }
  })
  const payoutRows = payouts.map(
    (payout): TransactionRow => ({
      key: `payout-${payout.period}`,
      kind: 'payout',
      label: KIND_LABELS.payout,
      date: payout.payout_date || payout.period,
      counterparty: null,
      amountCents: Math.round(payout.amount * 100),
      currency: 'USD',
      direction: 'payout',
      status: PAYOUT_STATUS[payout.status] ?? 'pending',
      description: null,
    })
  )
  return [...payments, ...payoutRows].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  )
}

export const KIND_LABELS: Record<TransactionKind, string> = {
  subscription: 'Subscription',
  tip: 'Tip',
  payout: 'Payout',
  refund: 'Refund',
  purchase: 'Purchase',
}

export const displayName = (user: UserBrief): string => user.full_name || user.username
